import React, { useContext, useState } from 'react';
import SectionContext from '../../SectionContext';
import FormWrapper from './FormWrapper';
import SectionNameInput from './SectionNameInput';
import CancelButton from '@/components/CancelButton';
import { IconButton } from '@mui/material';
import { Check as CheckIcon } from '@mui/icons-material';
import { useIntl } from 'react-intl';
import useApi from '@/lib/api/useApi';
import ModelsContext from '@/pages/EditorPage/ChallengeModelsEditor/ModelsContext';
import { type EditorSectionWithoutRelations } from '@/lib/types';
import useComplexSwrState from '@/lib/utils/useComplexSwrState';
import SelectedModelIdContext from '@/pages/EditorPage/ChallengeModelsEditor/SelectedModelIdContext';

type SectionNameEditorProps = {
  onClose: () => void;
};

const SectionNameEditor = ({ onClose }: SectionNameEditorProps) => {
  const api = useApi();

  const [, setModels] = useContext(ModelsContext);
  const selectedModelId = useContext(SelectedModelIdContext);

  const { section } = useContext(SectionContext);
  const [newSectionName, setNewSectionName, , reenableNewSectionNameSync] =
    useComplexSwrState(section.name);

  const [saving, setSaving] = useState(false);

  const saveNameChange = async (
    e: React.SubmitEvent<HTMLFormElement>,
  ): Promise<void> => {
    e.preventDefault();

    if (!newSectionName.trim()) {
      return;
    }

    setSaving(true);

    const updatedSection = await api.put<EditorSectionWithoutRelations>(
      `editor/challenge-model/sections/${section.id}/name`,
      {
        name: newSectionName.trim(),
      },
    );

    setModels(models => {
      models[selectedModelId].sections[section.id] = {
        ...models[selectedModelId].sections[section.id],
        ...updatedSection,
      };
    });

    setSaving(false);
    onClose();

    reenableNewSectionNameSync();
  };

  const cancelNameChange = () => {
    onClose();

    setNewSectionName(section.name);
    reenableNewSectionNameSync();
  };

  const intl = useIntl();

  return (
    <FormWrapper className="section-name-form" onSubmit={saveNameChange}>
      <SectionNameInput
        placeholder={intl.formatMessage({
          id: 'editor.model_editor.section_editor.edit_name.input_placeholder',
        })}
        value={newSectionName}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setNewSectionName(e.target.value)
        }
        onEscPress={cancelNameChange}
        autoFocus
      />
      <CancelButton
        visible={newSectionName !== section.name}
        onClick={cancelNameChange}
        disabled={saving}
      />
      <IconButton
        edge="end"
        type="submit"
        disabled={!newSectionName.trim() || saving}
      >
        <CheckIcon />
      </IconButton>
    </FormWrapper>
  );
};

export default SectionNameEditor;
